import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Container from '@mui/material/Container';
// Iconos más apropiados para cada función
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { CircularProgress } from '@mui/material';
import HttpsIcon from '@mui/icons-material/Https';

import { message } from 'antd';

import LoadingInformation from '../Components/LoadingInformation';
import '../../Styles/Menu/PointOfSale.css'

const FinancesMenu = () => {
  const [messageApi, contextHolder] = message.useMessage();

  const [permissionError, setPermissionError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const navigate = useNavigate();

  const checkPermissions = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.error('No se encontró el token de autenticación.');
      setPermissionError(true);
      window.location.href = '/sign-in'; // Redirect to login if no token
      return;
    }
    setPermissionError(false);
    setIsLoading(true);
    try {
      const response = await fetch('https://dprincess-training.servehttp.com/users/check-modify-role-permissions', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.status === 403) {
        messageApi.error('No tienes permisos para acceder a esta sección.');
        setPermissionError(true);
        throw new Error('No tienes permisos para acceder a esta sección.');
      }
    } catch (error) {
      messageApi.error(error.message || 'Error al verificar permisos.');
      throw error; // Re-throw to handle it in the calling function
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const cardStyle = {
    maxWidth: 275,
    height: 180,
    boxShadow: '0 4px 12px rgba(83, 29, 171, 0.15)',
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 24px rgba(83, 29, 171, 0.25)',
    }
  };

  const iconStyle = {
    fontSize: 40,
    color: '#531dab',
    opacity: 0.8
  };

  if (isLoading) {
    return <LoadingInformation />;
  }

  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        {contextHolder}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <div className='pointofsale-main-container'>
      <div className='pointofsale-container'>
        <div className='cashflow-opt-container grid-obj'>
          <Card sx={cardStyle} onClick={() => navigate('/finances/cash-flow')}>
            <CardActionArea sx={{ height: 180 }}>
              <CardContent sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 150,
                justifyContent: 'space-between',
                padding: '20px'
              }}>
                <div className='content-container'>
                  <Typography sx={{
                    color: '#531dab',
                    fontWeight: 'bold',
                    marginBottom: '8px'
                  }} gutterBottom variant="h5" component="div">
                    Flujo de Efectivo
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    lineHeight: 1.4
                  }}>
                    Registrar entradas y salidas de dinero.
                  </Typography>
                </div>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <AccountBalanceWalletIcon sx={iconStyle} />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </div>
        <div className='bills-receivable-opt-container grid-obj'>
          <Card sx={cardStyle} onClick={() => navigate('/finances/daily-cash-report')}>
            <CardActionArea sx={{ height: 180 }}>
              <CardContent sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 150,
                justifyContent: 'space-between',
                padding: '20px'
              }}>
                <div className='content-container'>
                  <Typography sx={{
                    color: '#531dab',
                    fontWeight: 'bold',
                    marginBottom: '8px'
                  }} gutterBottom variant="h5" component="div">
                    Reporte diario de caja chica
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    lineHeight: 1.4
                  }}>
                    Visualizar el reporte diario de la caja chica.
                  </Typography>
                </div>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ReceiptIcon sx={iconStyle} />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </div>
        <div className='statement-cash-flows-resume-opt-container grid-obj'>
          <Card sx={cardStyle}>
            <CardActionArea sx={{ height: 180 }}>
              <CardContent sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 150,
                justifyContent: 'space-between',
                padding: '20px'
              }}>
                <div className='content-container'>
                  <Typography sx={{
                    color: '#531dab',
                    fontWeight: 'bold',
                    marginBottom: '8px'
                  }} gutterBottom variant="h5" component="div">
                    Flujos de tesorería
                  </Typography>
                  <Typography variant="body2" sx={{
                    color: 'text.secondary',
                    lineHeight: 1.4
                  }}>
                    Visualizar resumen de ingresos y egresos.
                  </Typography>
                </div>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <AssessmentIcon sx={iconStyle} />
                </Box>
              </CardContent>
            </CardActionArea>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FinancesMenu;